import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ROUTE_PATH } from '../../../routes/route-paths';
import { useLockBody } from '../../../hooks/utils/useLockBody';

interface CreateAccountGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  redirectPath?: string;
}

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M15 5L5 15M5 5L15 15"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CreateAccountGateModal = ({ isOpen, onClose, redirectPath }: CreateAccountGateModalProps) => {
  const history = useHistory();

  useLockBody(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const redirect = redirectPath || `${history.location.pathname}${history.location.search}`;
  const redirectQuery = `redirect=${encodeURIComponent(redirect)}`;
  const signupUrl = `${ROUTE_PATH.SIGNUP}?${redirectQuery}`;
  const loginUrl = `${ROUTE_PATH.LOGIN}?${redirectQuery}`;

  return (
    <div
      className="bg-modalBackdropColor fixed inset-0 z-40 flex items-center justify-center px-4 py-6 backdrop-blur-sm backdrop-filter"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-account-gate-title"
        aria-describedby="create-account-gate-description"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-label-gray absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-gray-50 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
        >
          <CloseIcon />
        </button>

        <div className="pr-10 text-center sm:text-left">
          <h2 id="create-account-gate-title" className="font-open-sans text-2xl font-semibold leading-tight text-black">
            Create a free account
          </h2>
          <p id="create-account-gate-description" className="font-open-sans mt-2 text-sm leading-relaxed text-gray-600">
            Sign up to schedule calls with peers, save favorites, and access your organization&apos;s full member
            experience.
          </p>
        </div>

        <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="button"
            onClick={() => history.push(loginUrl)}
            className="text-primary bg-button-secondary hover:bg-button-secondary-hover flex flex-1 items-center justify-center rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] transition-colors"
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => history.push(signupUrl)}
            className="bg-button-primary hover:bg-button-primary-hover flex flex-1 items-center justify-center rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] transition-colors"
          >
            Sign Up
          </button>
        </div>
      </div>
    </div>
  );
};
